import { AttackPattern, PatternState } from '../AttackPattern.js';

let nextRepastToken = 1;

const INHALE_DURATION = 1.4;
const INHALE_SPEED = 170; // how fast captured bullets are dragged back to the maw
const SPIT_INTERVAL = 0.11;

/**
 * The Repast (The Famished Court) — the Court eats its own barrage. Every
 * bullet it has in flight is snapped around and dragged back into the maw;
 * each swallowed pair is re-served as one fast gobbet aimed straight at
 * the player. The inversion is the mechanic: a screen you've carefully
 * left cluttered (weaving between slow rings instead of clearing distance)
 * suddenly converts into aimed fire — the messier the plate, the worse the
 * second course. Dodging the inhale itself is trivial (bullets rushing
 * AWAY from you); the read is repositioning before the spit begins.
 *
 * Ends by overeating: a big enough meal opens a vulnerability window —
 * the counterweight to the Feast's healing economy.
 */
export class TheRepast extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'The Repast', telegraphDuration: 0.9, activeDuration: 3.8, minPhase });
    this.config = config;
  }

  onBegin() {
    this._token = nextRepastToken++;
    this._elapsed = 0;
    this._captured = [];
    this._eaten = 0;
    this._spitsLeft = 0;
    this._spitTimer = 0;
    this._spitting = false;
  }

  onActiveStart(_boss, _player, pool) {
    // Snapshot of everything in flight right now — bullets fired mid-inhale
    // by a layered pattern stay free, same one-clean-capture rule as the
    // Grand Pause.
    pool.forEachActive((bullet) => {
      if (bullet._grandPaused) return; // never steal from a live caesura
      bullet._repastToken = this._token;
      this._captured.push(bullet);
    });
  }

  onActiveTick(dt, boss, player, pool) {
    this._elapsed += dt;

    if (this._elapsed < INHALE_DURATION) {
      // Drag every captured bullet toward the maw, swallowing on arrival.
      for (const bullet of this._captured) {
        if (!bullet.active || bullet._repastToken !== this._token) continue;
        const dx = boss.x - bullet.x;
        const dy = boss.y - bullet.y;
        const dist = Math.hypot(dx, dy) || 1;
        if (dist < boss.radius * 0.7) {
          bullet.active = false;
          this._eaten++;
          continue;
        }
        const speed = INHALE_SPEED + this._elapsed * 130; // the suction builds
        bullet.vx = (dx / dist) * speed;
        bullet.vy = (dy / dist) * speed;
        bullet.speed = speed;
        bullet.accel = 0;
      }
      return;
    }

    if (!this._spitting) {
      this._spitting = true;
      // Whatever wasn't swallowed in time goes free again.
      for (const bullet of this._captured) {
        if (bullet._repastToken === this._token) bullet._repastToken = null;
      }
      // A large meal should make the second course scarier, not create an
      // unreadable aimed-bullet machine when layered patterns are active.
      const maxSpits = this.config.maxSpits ?? 8;
      this._spitsLeft = Math.min(maxSpits, this.config.baseSpits + Math.ceil(this._eaten / 2));
      this._spitTimer = 0;
    }

    this._spitTimer -= dt;
    while (this._spitTimer <= 0 && this._spitsLeft > 0) {
      this._spitTimer += SPIT_INTERVAL;
      this._spitsLeft--;
      const jitter = (Math.random() - 0.5) * 0.22;
      const angle = Math.atan2(player.y - boss.y, player.x - boss.x) + jitter;
      pool.fire({
        x: boss.x,
        y: boss.y,
        vx: Math.cos(angle) * this.config.spitSpeed,
        vy: Math.sin(angle) * this.config.spitSpeed,
        radius: 3.2,
        damage: this.config.spitDamage,
        color: '#cdd45a',
        lifetime: 3.2,
      });
    }
  }

  onEnd(boss) {
    // Defensive untag (normally already cleared at the spit transition).
    for (const bullet of this._captured) {
      if (bullet._repastToken === this._token) bullet._repastToken = null;
    }
    // Overate — a big meal leaves the Court bloated and open.
    if (this._eaten >= this.config.overeatThreshold) {
      boss.openVulnerability(0.8, 1.6);
    }
  }

  getVisual(boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return { kind: 'repastMaw', x: boss.x, y: boss.y, t: 1 - this._timer / this.telegraphDuration, inhaling: false };
    }
    if (this.state === PatternState.ACTIVE && this._elapsed < INHALE_DURATION) {
      return { kind: 'repastMaw', x: boss.x, y: boss.y, t: this._elapsed / INHALE_DURATION, inhaling: true };
    }
    return null;
  }
}
